import React, { useState, useCallback } from 'react';
import { sendContactEmail } from '../services/gemini';
import HCaptcha, { HCAPTCHA_SITE_KEY } from './HCaptcha';
import '../styles/Sections.css';

// ─── Hero ─────────────────────────────────────────────────────────────────────
export function Hero() {
  return (
    <section className="hero" id="inicio">
      <div className="hero-bg-glow" aria-hidden />
      <div className="hero-content">
        <div className="hero-badge">
          <span className="hero-badge-dot" aria-hidden />
          Tecnologia para o Campo
        </div>
        <h1 className="hero-title">
          O futuro do<br />
          <em>agro familiar</em><br />
          começa com <span className="hero-accent">dados</span>
        </h1>
        <p className="hero-sub">
          Digitalizamos a vida do pequeno produtor rural com soluções simples,
          acessíveis e feitas para o dia a dia do campo.
        </p>
        <div className="hero-actions">
          <a href="#solucoes" className="btn-primary">Conhecer soluções →</a>
          <a href="#contato" className="btn-secondary">Falar com especialista</a>
        </div>
      </div>
      <div className="hero-visual" aria-hidden>
        <div className="hero-card">
          <div className="hero-stats">
            {[
              { num: '+500', label: 'Produtores atendidos' },
              { num: '32%', label: 'Ganho de produtividade', amber: true },
              { num: 'IoT', label: 'Sensores no campo' },
              { num: 'Cloud', label: 'Infra escalável', amber: true },
            ].map((s, i) => (
              <div className="stat-box" key={i}>
                <div className={`stat-num${s.amber ? ' amber' : ''}`}>{s.num}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            ))}
          </div>
          <div className="feature-list">
            {[
              { text: 'Software de gestão rural acessível', amber: false },
              { text: 'Consultoria para equipamentos eletrônicos', amber: true },
              { text: 'Infraestrutura IoT para monitoramento', amber: false },
              { text: 'Micro SaaS para pequenas propriedades', amber: true },
            ].map((f, i) => (
              <div className="feature-item" key={i}>
                <span className={`feature-dot${f.amber ? ' amber' : ''}`} />
                {f.text}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Soluções ─────────────────────────────────────────────────────────────────
const SOLUTIONS = [
  { icon: '📱', title: 'Software de Gestão Rural', desc: 'Aplicativo intuitivo para controle de safra, estoque, finanças e produção. Feito para quem não tem tempo a perder.', tag: 'SaaS' },
  { icon: '📡', title: 'Infraestrutura IoT', desc: 'Sensores de solo, clima e irrigação conectados ao seu celular. Tome decisões com dados reais do seu campo, em tempo real.', tag: 'IoT' },
  { icon: '☁️', title: 'Cloud para o Agro', desc: 'Armazenamento e processamento de dados em nuvem segura e escalável. Seus dados do campo, disponíveis em qualquer lugar.', tag: 'Cloud' },
  { icon: '🔧', title: 'Consultoria em Equipamentos', desc: 'Orientamos na escolha e aquisição de equipamentos eletrônicos de pequeno porte que realmente fazem diferença no dia a dia.', tag: 'Consultoria' },
  { icon: '🌾', title: 'Digitalização da Propriedade', desc: 'Mapeamento digital da sua terra, controle de talhões e histórico de plantio. Do papel para o digital, sem complicação.', tag: 'Digital' },
  { icon: '📊', title: 'Análise e Relatórios', desc: 'Dashboards simples com os indicadores que importam para você. Tome decisões melhores com informação clara e visual.', tag: 'Analytics' },
];

export function Solucoes() {
  return (
    <section className="section solucoes-section" id="solucoes">
      <div className="container">
        <div className="section-label">Nossas Soluções</div>
        <h2 className="section-title">Tecnologia que <em>entende o campo</em></h2>
        <p className="section-sub">
          Desenvolvemos ferramentas sob medida para a realidade do produtor familiar
          brasileiro — simples de usar, poderosas nos resultados.
        </p>
        <div className="solutions-grid">
          {SOLUTIONS.map((s, i) => (
            <div className="sol-card" key={i}>
              <div className="sol-icon">{s.icon}</div>
              <h3 className="sol-title">{s.title}</h3>
              <p className="sol-desc">{s.desc}</p>
              <span className="sol-tag">{s.tag}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Sobre ────────────────────────────────────────────────────────────────────
const PILLARS = [
  { icon: '🌱', title: 'Simples', desc: 'Tecnologia que qualquer produtor consegue usar' },
  { icon: '⚡', title: 'Ágil', desc: 'Soluções rápidas de implementar e usar no campo' },
  { icon: '💚', title: 'Acessível', desc: 'Preços justos para a agricultura familiar' },
  { icon: '📈', title: 'Escalável', desc: 'Cresce junto com o seu negócio' },
];

export function Sobre() {
  return (
    <section className="section sobre-section" id="sobre">
      <div className="container">
        <div className="section-label">Sobre a GB Agrotech</div>
        <div className="about-grid">
          <div>
            <h2 className="section-title" style={{ color: 'var(--white)' }}>
              Somos do campo.<br />Pensamos em <em>tecnologia</em>.
            </h2>
            <p className="section-sub" style={{ color: 'rgba(255,255,255,0.55)' }}>
              A GB Agrotech nasceu do entendimento de que o produtor familiar merece
              as mesmas ferramentas tecnológicas que as grandes corporações —
              adaptadas à sua realidade.
            </p>
            <div className="about-pillars">
              {PILLARS.map((p, i) => (
                <div className="pillar" key={i}>
                  <div className="pillar-icon">{p.icon}</div>
                  <div className="pillar-title">{p.title}</div>
                  <div className="pillar-desc">{p.desc}</div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <blockquote className="about-quote">
              "Queremos que cada pequeno produtor tenha o poder da{' '}
              <em>tecnologia</em> na palma da mão — do solo ao celular."
            </blockquote>
            <p className="about-quote-attr">— Equipe GB Agrotech</p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Contato ──────────────────────────────────────────────────────────────────
export function Contato() {
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ nome: '', tel: '', email: '', msg: '' });
  const [captchaToken, setCaptchaToken] = useState('');

  const clearCaptcha = useCallback(() => setCaptchaToken(''), []);

  const captchaRequired = Boolean(HCAPTCHA_SITE_KEY);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (captchaRequired && !captchaToken) {
      setError('Confirme que você não é um robô antes de enviar.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await sendContactEmail({ ...form, captchaToken });
      setSent(true);
    } catch (err) {
      console.error('Erro ao enviar contato:', err);
      setError('Não foi possível enviar sua mensagem. Tente novamente ou fale pelo WhatsApp.');
      clearCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="section contato-section" id="contato">
      <div className="container">
        <div className="section-label">Fale Conosco</div>
        <h2 className="section-title">Pronto para <em>digitalizar</em> seu campo?</h2>
        <div className="contact-grid">
          <div className="contact-form-card">
            {sent ? (
              <div className="form-success">
                <div className="form-success-icon">✅</div>
                <h3>Mensagem enviada!</h3>
                <p>Nossa equipe entrará em contato em até 24h úteis.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {[
                  { label: 'Nome completo', key: 'nome', type: 'text', placeholder: 'Seu nome' },
                  { label: 'WhatsApp / Telefone', key: 'tel', type: 'tel', placeholder: '(00) 00000-0000' },
                  { label: 'E-mail', key: 'email', type: 'email', placeholder: 'seu@email.com' },
                ].map(f => (
                  <div className="form-group" key={f.key}>
                    <label className="form-label">{f.label}</label>
                    <input
                      className="form-input"
                      type={f.type}
                      placeholder={f.placeholder}
                      value={form[f.key]}
                      onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                      required
                    />
                  </div>
                ))}
                <div className="form-group">
                  <label className="form-label">Mensagem</label>
                  <textarea
                    className="form-input"
                    placeholder="Conte sobre sua propriedade e o que você precisa..."
                    rows={4}
                    value={form.msg}
                    onChange={e => setForm(prev => ({ ...prev, msg: e.target.value }))}
                  />
                </div>
                <HCaptcha onVerify={setCaptchaToken} onExpire={clearCaptcha} />
                {error && (
                  <p style={{ color: '#e05252', fontSize: '0.875rem', marginBottom: '0.75rem' }}>{error}</p>
                )}
                <button
                  type="submit"
                  className="btn-primary"
                  style={{ width: '100%', justifyContent: 'center', borderRadius: 'var(--radius-md)' }}
                  disabled={loading || (captchaRequired && !captchaToken)}
                >
                  {loading ? 'Enviando...' : 'Enviar mensagem →'}
                </button>
              </form>
            )}
          </div>
          <div className="contact-info">
            {[
              { icon: '📧', title: 'E-mail', val: 'vendas@gbagrotech.com', href: 'mailto:vendas@gbagrotech.com' },
              { icon: '🌾', title: 'Atendemos todo o Brasil', val: 'Foco em pequenos e médios produtores da agricultura familiar' },
            ].map((c, i) => (
              <div className="info-card" key={i}>
                <div className="info-icon">{c.icon}</div>
                <div>
                  <div className="info-title">{c.title}</div>
                  <div className="info-val">{c.href ? <a href={c.href} style={{ color: 'inherit' }}>{c.val}</a> : c.val}</div>
                </div>
              </div>
            ))}
            <div className="info-card">
              <div className="info-icon">💬</div>
              <div>
                <div className="info-title">WhatsApp</div>
                <div className="info-val">
                  <a href="https://wa.me/5595936180012" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                    (95) 93618-0012
                  </a>
                </div>
              </div>
            </div>
            <a
              href="https://wa.me/5595936180012"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center', borderRadius: 'var(--radius-md)', textDecoration: 'none' }}
            >
              <span>💬</span> Falar no WhatsApp
            </a>
            <div className="chat-promo-card">
              <div className="chat-promo-title">💬 Fale com a Ágata</div>
              <p className="chat-promo-desc">
                Nossa atendente virtual faz a triagem em minutos e já direciona você
                para o consultor certo.
              </p>
              <a href="#inicio" className="btn-primary" style={{ fontSize: '0.85rem', padding: '0.65rem 1.4rem' }}
                onClick={e => { e.preventDefault(); document.querySelector('.chat-fab')?.click(); }}>
                Abrir chat →
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────
export function Footer() {
  return (
    <footer className="footer">
      <div className="footer-logo">GB <span>Agro</span>tech</div>
      <p className="footer-copy">© {new Date().getFullYear()} GB Agrotech. Todos os direitos reservados.</p>
      <p className="footer-copy" style={{ fontSize: '0.78rem', opacity: 0.55, marginTop: '0.25rem' }}>
        CNPJ 43.929.176/0001-35 &nbsp;·&nbsp;{' '}
        <a href="https://wa.me/5595936180012" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
          (95) 93618-0012
        </a>
      </p>
      <div className="footer-links">
        <a href="#inicio">Início</a>
        <a href="#solucoes">Soluções</a>
        <a href="#contato">Contato</a>
      </div>
    </footer>
  );
}
